import React, { useEffect, useState } from 'react';
import { getEmployees, getDepartments, createEmployee, updateEmployee, removeEmployee, resetEmployeePassword, getRemovedEmployees, restoreEmployee } from '../services/employeeService';
import useAuthStore from '../store/authStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import useUIStore from '../store/uiStore';
import ScrollX from '../components/ScrollX';

const ROLES = [
  'HOD', 'Manager', 'Supervisor', 'Engineer', 'Worker', 'Site_Lead', 'Inspector',
  // Added for the KRA module - see app/models.py Employee.role comment
  'CEO', 'HR', 'System_Admin', 'External_Reviewer',
];

const initialForm = {
  name: '', email: '', password: '', role: 'Worker', department_id: '', salary_per_month: '', phone: '',
};

const PeoplePage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [view, setView] = useState('active'); // 'active' | 'removed' (removed view is HR / CEO only)
  const [removed, setRemoved] = useState([]);
  const { user } = useAuthStore();
  // Same people the backend lets remove/restore (require_hr_or_ceo): CEO, HR,
  // and the HOD heading the HR department. UI convenience only.
  const canManageRemoval = !!user && (['CEO', 'HR'].includes(user.role) || (user.role === 'HOD' && (user.department || '').trim().toLowerCase() === 'hr'));
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    Promise.all([getEmployees(), getDepartments()])
      .then(([empRes, deptRes]) => {
        setEmployees(empRes.data.employees || []);
        setDepartments(deptRes.data.departments || []);
      })
      .catch(() => showToast('Failed to load people', 'error'))
      .finally(() => setLoading(false));
    if (canManageRemoval) {
      getRemovedEmployees()
        .then((res) => setRemoved(res.data.employees || []))
        .catch(() => {}); // the Active view still works; only the Removed count/list is missing
    }
  };

  useEffect(load, [canManageRemoval]); // eslint-disable-line react-hooks/exhaustive-deps

  const departmentName = (id) => departments.find((d) => d.id === id)?.name || '—';

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.department_id || !form.salary_per_month) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await createEmployee({
        name: form.name,
        email: form.email || null,
        password: form.password || null,
        role: form.role,
        department_id: Number(form.department_id),
        salary_per_month: Number(form.salary_per_month),
        phone: form.phone || null,
      });
      showToast('Person added', 'success');
      setForm(initialForm);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add person', 'error');
    }
    setSubmitting(false);
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setEditForm({
      name: e.name, role: e.role, department_id: e.department_id, salary_per_month: e.salary_per_month, phone: e.phone || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (id) => {
    try {
      await updateEmployee(id, {
        name: editForm.name,
        role: editForm.role,
        department_id: Number(editForm.department_id),
        salary_per_month: Number(editForm.salary_per_month),
        phone: editForm.phone || null,
      });
      showToast('Person updated', 'success');
      cancelEdit();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update person', 'error');
    }
  };

  const handleResetPassword = async (e) => {
    const newPassword = window.prompt(`Set a new password for ${e.name} (min 6 characters). They'll need to be told this directly - there's no way to email it to them.`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    try {
      await resetEmployeePassword(e.id, newPassword);
      showToast(`Password reset for ${e.name}`, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to reset password', 'error');
    }
  };

  const handleRemove = async (e) => {
    if (!window.confirm(`Remove ${e.name}? They'll be hidden from active lists but their past work stays on record.`)) {
      return;
    }
    try {
      await removeEmployee(e.id);
      showToast('Person removed', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to remove person', 'error');
    }
  };

  const handleRestore = async (e) => {
    try {
      await restoreEmployee(e.id);
      showToast(`${e.name} restored`, 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to restore person', 'error');
    }
  };

  const formatRemovedAt = (iso) => {
    if (!iso) return 'Not recorded';
    const t = new Date(iso);
    return `${formatDateDDMMYY(iso.slice(0, 10))}, ${t.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}`;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-6">People</h1>

        <form onSubmit={handleSubmit} className="glass-card p-6 mb-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Add Person</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                required
                value={form.name}
                onChange={handleChange('name')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Only needed if they'll log in"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Login Password</label>
              <input
                type="text"
                value={form.password}
                onChange={handleChange('password')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Only needed if they'll log in"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                value={form.phone}
                onChange={handleChange('phone')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <select
                value={form.role}
                onChange={handleChange('role')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
              <select
                required
                value={form.department_id}
                onChange={handleChange('department_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select a department</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary per Month (₹) *</label>
              <input
                required
                type="number"
                value={form.salary_per_month}
                onChange={handleChange('salary_per_month')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
          >
            {submitting ? 'Adding...' : 'Add Person'}
          </button>
        </form>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-semibold text-gray-900">
              {view === 'removed' && canManageRemoval ? 'Removed People' : 'All People'}
            </h2>
            {canManageRemoval && (
              <div className="inline-flex rounded-full border border-gray-200 overflow-hidden text-sm" role="group" aria-label="View">
                <button
                  onClick={() => setView('active')}
                  className={`px-4 py-1 ${view === 'active' ? 'bg-komal-blue text-white font-semibold' : 'text-gray-600'}`}
                >
                  Active <span className="opacity-80">{employees.length}</span>
                </button>
                <button
                  onClick={() => setView('removed')}
                  className={`px-4 py-1 ${view === 'removed' ? 'bg-komal-blue text-white font-semibold' : 'text-gray-600'}`}
                >
                  Removed <span className="opacity-80">{removed.length}</span>
                </button>
              </div>
            )}
            {!canManageRemoval && <span className="text-sm text-gray-500">{employees.length}</span>}
          </div>
          {loading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : view === 'removed' && canManageRemoval ? (
            <>
              <p className="mx-5 my-3 text-xs bg-amber-50 text-amber-900 rounded px-3 py-2">
                They are hidden from daily entry, assignments and dropdowns. All their past work stays on record. Restore puts a person back.
              </p>
              {removed.length === 0 ? (
                <p className="text-gray-500 text-sm px-5 pb-5">Nobody is removed.</p>
              ) : (
                <ScrollX>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-100">
                      <th className="px-5 py-2 font-medium">Name</th>
                      <th className="px-5 py-2 font-medium">Role</th>
                      <th className="px-5 py-2 font-medium">Department</th>
                      <th className="px-5 py-2 font-medium">Removed on</th>
                      <th className="px-5 py-2 font-medium">Removed by</th>
                      <th className="px-5 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {removed.map((e) => (
                      <tr key={e.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-5 py-3">{e.name}</td>
                        <td className="px-5 py-3">{e.role}</td>
                        <td className="px-5 py-3">{departmentName(e.department_id)}</td>
                        <td className={`px-5 py-3 ${e.removed_at ? '' : 'text-gray-400'}`}>{formatRemovedAt(e.removed_at)}</td>
                        <td className={`px-5 py-3 ${e.removed_by_name ? '' : 'text-gray-400'}`}>{e.removed_by_name || 'Not recorded'}</td>
                        <td className="px-5 py-3">
                          <button onClick={() => handleRestore(e)} className="bg-komal-blue hover:bg-komal-blue-dark text-white text-xs font-semibold px-3 py-1 rounded">
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </ScrollX>
              )}
            </>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">Name</th>
                  <th className="px-5 py-2 font-medium">Email</th>
                  <th className="px-5 py-2 font-medium">Role</th>
                  <th className="px-5 py-2 font-medium">Department</th>
                  <th className="px-5 py-2 font-medium">Salary/Month</th>
                  <th className="px-5 py-2 font-medium">Phone</th>
                  <th className="px-5 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) =>
                  editingId === e.id ? (
                    <tr key={e.id} className="border-b border-gray-50 last:border-0 bg-gray-50">
                      <td className="px-5 py-2">
                        <input
                          value={editForm.name}
                          onChange={(ev) => setEditForm({ ...editForm, name: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-5 py-2 text-gray-400">{e.email || '—'}</td>
                      <td className="px-5 py-2">
                        <select
                          value={editForm.role}
                          onChange={(ev) => setEditForm({ ...editForm, role: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-2">
                        <select
                          value={editForm.department_id || ''}
                          onChange={(ev) => setEditForm({ ...editForm, department_id: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-2">
                        <input
                          type="number"
                          value={editForm.salary_per_month}
                          onChange={(ev) => setEditForm({ ...editForm, salary_per_month: ev.target.value })}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-5 py-2">
                        <input
                          value={editForm.phone}
                          onChange={(ev) => setEditForm({ ...editForm, phone: ev.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-5 py-2 space-x-2 whitespace-nowrap">
                        <button onClick={() => saveEdit(e.id)} className="text-green-700 hover:underline text-xs font-semibold">Save</button>
                        <button onClick={cancelEdit} className="text-gray-500 hover:underline text-xs font-semibold">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={e.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-5 py-3">{e.name}</td>
                      <td className="px-5 py-3 text-gray-500">{e.email || '—'}</td>
                      <td className="px-5 py-3">{e.role}</td>
                      <td className="px-5 py-3">{departmentName(e.department_id)}</td>
                      <td className="px-5 py-3">₹{Number(e.salary_per_month).toLocaleString()}</td>
                      <td className="px-5 py-3 text-gray-500">{e.phone || '—'}</td>
                      <td className="px-5 py-3 space-x-2 whitespace-nowrap">
                        <button onClick={() => startEdit(e)} className="text-komal-blue hover:underline text-xs font-semibold">Edit</button>
                        {e.email && (
                          <button onClick={() => handleResetPassword(e)} className="text-amber-600 hover:underline text-xs font-semibold">Reset Password</button>
                        )}
                        {canManageRemoval && (
                          <button onClick={() => handleRemove(e)} className="text-red-600 hover:underline text-xs font-semibold">Remove</button>
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeoplePage;
